/**
 * useApi — binds the customer API functions to an Entra bearer token acquired via
 * MSAL (silent, popup fallback), mirroring useUserRole.
 */
import { useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiScopes } from '@/auth/authConfig';
import * as customers from '@/api/customers';
import * as admin from '@/api/admin';
import * as promotions from '@/api/promotions';
import * as comms from '@/api/comms';

export function useApi() {
  const { instance, accounts } = useMsal();

  return useMemo(() => {
    const getHeaders: customers.HeadersGetter = async () => {
      const account = accounts[0];
      if (!account) throw new Error('Not signed in');
      try {
        const res = await instance.acquireTokenSilent({ scopes: apiScopes, account });
        return { Authorization: `Bearer ${res.accessToken}` };
      } catch (err) {
        // No popup — fall back to a full-page redirect for re-consent/re-auth.
        if (err instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect({ scopes: apiScopes, account });
        }
        throw err;
      }
    };

    return {
      list: (limit: number, offset: number, signal?: AbortSignal, opts?: customers.CustomerListOptions) =>
        customers.listCustomers(getHeaders, limit, offset, signal, opts),
      stores: () => customers.listStores(getHeaders),
      search: (q: string, signal?: AbortSignal) => customers.searchCustomers(getHeaders, q, signal),
      get: (id: string) => customers.getCustomer(getHeaders, id),
      update: (id: string, patch: Parameters<typeof customers.updateCustomer>[2]) =>
        customers.updateCustomer(getHeaders, id, patch),
      setInterest: (id: string, tag: string, on: boolean) =>
        customers.setInterest(getHeaders, id, tag, on),
      home: () => customers.getHomeSummary(getHeaders),
      searchProducts: (q: string, signal?: AbortSignal) => customers.searchProducts(getHeaders, q, signal),
      addFavourite: (id: string, fav: { sku: string; productName: string; note?: string }) =>
        customers.addFavourite(getHeaders, id, fav),
      removeFavourite: (id: string, favId: string) => customers.removeFavourite(getHeaders, id, favId),
      addKeyDate: (id: string, kd: { type: string; label?: string; month: number; day: number; year?: number; source?: string }) =>
        customers.addKeyDate(getHeaders, id, kd),
      removeKeyDate: (id: string, kdId: string) => customers.removeKeyDate(getHeaders, id, kdId),
      check: (email: string, phone: string) => customers.checkDuplicate(getHeaders, email, phone),
      transactions: (id: string, limit: number, offset: number) =>
        customers.getTransactions(getHeaders, id, limit, offset),
      createShopifyIdentity: (id: string) => customers.createShopifyIdentity(getHeaders, id),
      addNote: (id: string, body: string, txn?: { storeNo: string; posTerminalNo: string; transactionNo: number }) =>
        customers.addNote(getHeaders, id, body, txn),
      create: (input: customers.CreateCustomerInput) => customers.createCustomer(getHeaders, input),
      // promotions
      promotions: (days?: number) => promotions.getPromotions(getHeaders, days),
      promoMatches: (days?: number) => promotions.getPromoMatches(getHeaders, days),
      addPromoReminder: (input: { customerId: string; plannerNo: string; itemNo: string }) =>
        promotions.addPromoReminder(getHeaders, input),
      // comms (email v1)
      commTemplates: () => comms.listTemplates(getHeaders),
      saveCommTemplate: (input: Parameters<typeof comms.saveTemplate>[1]) => comms.saveTemplate(getHeaders, input),
      deleteCommTemplate: (id: string) => comms.deleteTemplate(getHeaders, id),
      promoteCommTemplate: (id: string) => comms.promoteTemplate(getHeaders, id),
      sendEmail: (input: Parameters<typeof comms.sendEmail>[1]) => comms.sendEmail(getHeaders, input),
      // admin → sync tab
      syncStatus: () => admin.getSyncStatus(getHeaders),
      runSync: (target: string) => admin.runSync(getHeaders, target),
      getSyncConfig: () => admin.getSyncConfig(getHeaders),
      setSyncConfig: (cron: string, enabled: boolean) => admin.setSyncConfig(getHeaders, cron, enabled),
      // admin → users & roles tab
      listUsers: () => admin.listUsers(getHeaders),
      createUser: (input: { email: string; displayName?: string; role: admin.AdminRole; storeCodes: string[] }) =>
        admin.createUser(getHeaders, input),
      updateUser: (id: string, patch: { role?: admin.AdminRole; storeCodes?: string[]; canEmail?: boolean }) =>
        admin.updateUser(getHeaders, id, patch),
      deleteUser: (id: string) => admin.deleteUser(getHeaders, id),
      reactivateUser: (id: string) => admin.reactivateUser(getHeaders, id),
    };
  }, [instance, accounts]);
}
